import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { LogOut, ChevronLeft, ChevronRight, ChevronDown, Menu, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import AlertCenter from '@/components/alerts/AlertCenter';
import { NAV_SECTIONS, canSee } from '@/lib/navigation';

const readStoredGroups = () => {
  try {
    return JSON.parse(localStorage.getItem('octopus_sidebar_groups')) ?? {};
  } catch {
    return {};
  }
};

const Sidebar = () => {
  const { t } = useTranslation();
  const { logout, isAdmin, hasPermission } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(readStoredGroups);

  const authCtx = { isAdmin, hasPermission };

  const handleLogout = () => {
    setMobileOpen(false);
    logout();
  };

  const handleGroupOpenChange = (groupId, value) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [groupId]: value };
      try {
        localStorage.setItem('octopus_sidebar_groups', JSON.stringify(next));
      } catch {
        // localStorage unavailable (private mode/quota) — intent simply isn't persisted this session
      }
      return next;
    });
  };

  const isItemActive = (item) =>
    location.pathname === item.path ||
    (item.path !== '/' && location.pathname.startsWith(`${item.path}/`));

  const handleSearchClick = () => {
    window.dispatchEvent(new CustomEvent('octopus:open-command-palette'));
    setMobileOpen(false);
  };

  const renderNavItem = (item, indent = false, isMobile = false) => {
    const isActive = isItemActive(item);
    const Icon = item.icon;
    const isRail = collapsed && !isMobile;

    const buttonClassName = cn(
      'w-full justify-start gap-3 transition-all duration-200',
      isActive && 'bg-primary/20 text-primary border-l-2 border-primary rounded-l-none',
      isRail && 'justify-center px-2',
      indent && !collapsed && 'pl-8'
    );
    const testId = `${isMobile ? 'mobile-' : ''}nav-${item.path.replace('/', '') || 'dashboard'}`;

    if (!isRail) {
      return (
        <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}>
          <Button variant={isActive ? 'secondary' : 'ghost'} className={buttonClassName} data-testid={testId}>
            <Icon className={cn('w-5 h-5', isActive && 'text-primary')} />
            <span>{item.label}</span>
          </Button>
        </Link>
      );
    }

    return (
      <Tooltip key={item.path}>
        <TooltipTrigger asChild>
          <Link to={item.path} onClick={() => setMobileOpen(false)}>
            <Button variant={isActive ? 'secondary' : 'ghost'} className={buttonClassName} data-testid={testId}>
              <Icon className={cn('w-5 h-5', isActive && 'text-primary')} />
            </Button>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    );
  };

  const renderGroup = (group, isMobile) => {
    const items = group.items.filter((item) => canSee(item, authCtx));
    if (items.length === 0) return null;

    const isActiveGroup = items.some((item) => isItemActive(item));
    const isOpen = !!openGroups[group.id] || isActiveGroup;
    const Icon = group.icon;

    return (
      <Collapsible
        key={group.id}
        open={isOpen}
        onOpenChange={(value) => handleGroupOpenChange(group.id, value)}
        className="space-y-1"
      >
        <CollapsibleTrigger asChild>
          <Button
            variant={isActiveGroup ? 'secondary' : 'ghost'}
            className={cn(
              'w-full justify-start gap-3 transition-all duration-200',
              isActiveGroup && 'bg-primary/10 text-primary'
            )}
            data-testid={`${isMobile ? 'mobile-' : ''}nav-${group.id}`}
          >
            <Icon className={cn('w-5 h-5', isActiveGroup && 'text-primary')} />
            <span className="flex-1 text-left">{group.label}</span>
            <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-1">
          {items.map((item) => renderNavItem(item, true, isMobile))}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  const renderSearchButton = (isMobile) => {
    const isRail = collapsed && !isMobile;

    if (!isRail) {
      return (
        <Button
          variant="ghost"
          className="w-full justify-start gap-3"
          onClick={handleSearchClick}
          data-testid={`${isMobile ? 'mobile-' : ''}nav-search`}
        >
          <Search className="w-5 h-5" />
          <span>Buscar…</span>
          <span className="ml-auto text-xs tracking-widest text-muted-foreground">⌘K</span>
        </Button>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            className="w-full justify-center px-2"
            onClick={handleSearchClick}
            data-testid="nav-search"
          >
            <Search className="w-5 h-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">Buscar (⌘K)</TooltipContent>
      </Tooltip>
    );
  };

  const renderSection = (section, isMobile) => {
    const visibleItems = (section.items || []).filter((item) => canSee(item, authCtx));
    const visibleGroups = (section.groups || []).filter(
      (group) => canSee(group, authCtx) && group.items.filter((item) => canSee(item, authCtx)).length > 0
    );

    if (visibleItems.length === 0 && visibleGroups.length === 0) return null;

    const isRail = collapsed && !isMobile;

    if (isRail) {
      return (
        <React.Fragment key={section.id}>
          <div className="h-px bg-border mx-2 my-1" />
          {visibleItems.map((item) => renderNavItem(item, false, isMobile))}
          {visibleGroups.map((group) =>
            group.items
              .filter((item) => canSee(item, authCtx))
              .map((item) => renderNavItem(item, false, isMobile))
          )}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={section.id}>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-3 pt-4 pb-1 select-none">
          {section.label}
        </div>
        {visibleItems.map((item) => renderNavItem(item, false, isMobile))}
        {visibleGroups.map((group) => renderGroup(group, isMobile))}
      </React.Fragment>
    );
  };

  const renderContent = (isMobile = false) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={cn(
        'flex items-center gap-3 p-4 border-b border-border',
        collapsed && !isMobile && 'justify-center'
      )}>
        <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl flex items-center justify-center overflow-hidden">
          <img
            src="/assets/octopus-icon.png"
            alt="Octopus"
            className="w-8 h-8 object-contain"
          />
        </div>
        {(!collapsed || isMobile) && (
          <span className="text-xl font-bold tracking-tight">Octopus</span>
        )}
        {(!collapsed || isMobile) && (
          <div className="ml-auto">
            <AlertCenter />
          </div>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 p-3">
        <TooltipProvider delayDuration={200}>
          <nav className="space-y-1">
            {renderSearchButton(isMobile)}
            {NAV_SECTIONS.map((section) => renderSection(section, isMobile))}
          </nav>
        </TooltipProvider>
      </ScrollArea>

      {/* Footer */}
      <div className="p-3 border-t border-border">
        <Button
          variant="ghost"
          className={cn(
            'w-full justify-start gap-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10',
            collapsed && !isMobile && 'justify-center px-2'
          )}
          onClick={handleLogout}
          data-testid={isMobile ? "mobile-logout-btn" : "logout-btn"}
        >
          <LogOut className="w-5 h-5" />
          {(!collapsed || isMobile) && <span>{t('logout')}</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-[60] md:hidden bg-background/80 backdrop-blur-sm shadow-md"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-5 h-5" />
      </Button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      {/* Mobile sidebar */}
      <aside className={cn(
        'fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-300 md:hidden',
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {renderContent(true)}
      </aside>

      {/* Desktop sidebar */}
      <aside className={cn(
        'hidden md:flex flex-col h-screen bg-card border-r border-border transition-all duration-300 relative',
        collapsed ? 'w-16' : 'w-64'
      )}>
        {renderContent(false)}

        {/* Collapse toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-card border border-border shadow-md"
          onClick={() => setCollapsed(!collapsed)}
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-3 h-3" />
          ) : (
            <ChevronLeft className="w-3 h-3" />
          )}
        </Button>
      </aside>
    </>
  );
};

export default Sidebar;
